import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  User,
  UserCredential,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult
} from 'firebase/auth';
import { onAuthChange, signInWithEmail, logOut, getIdToken, completeMagicLinkIfPresent } from '@/config/firebase';
import { getFirebaseAuth } from '@/config/firebase-init';
import { initializeFirebase } from '@/config/firebase-init';
import { api } from '@/config/api';
import { authApi } from '@/services/api/auth.api';
import { startTokenMonitoring, stopTokenMonitoring } from '@/services/token-monitor.service';
import toast from 'react-hot-toast';
import { useAppDispatch } from '@/hooks/redux';
import { hydrateCoaching } from '@/store/slices/coachingSlice';
import { userProgressService } from '@/services/userProgress.service';
import { GapDiscoveryService } from '@/services/gapDiscoveryService';

// Gate PII logging behind dev mode — in production builds this is a no-op
const authLog = import.meta.env.DEV ? console.log.bind(console) : () => {};

// Custom claims interface
export interface CustomClaims {
  role?: 'admin' | 'instructor' | 'student';
  tenantId?: string;
  permissions?: string[];
  isSuperAdmin?: boolean;
  // Standard OIDC claims on the Firebase ID token (not custom claims, but delivered
  // alongside them). `name` is the OAuth account's display name — the fallback when
  // neither the backend profile nor firebase user.displayName is populated.
  name?: string;
  email?: string;
}

// User profile from our backend
export interface UserProfile {
  id: string;
  email: string;
  displayName: string;
  role: 'admin' | 'instructor' | 'student';
  tenantId: string;
  photoUrl?: string;
  metadata?: Record<string, any>;
}

// Auth context type
interface AuthContextType {
  // Firebase user
  user: User | null;
  // Custom claims from token
  userClaims: CustomClaims | null;
  // User profile from backend
  userProfile: UserProfile | null;
  // Loading states
  loading: boolean;
  profileLoading: boolean;
  // Error state
  error: string;
  // Auth methods
  login: (email: string, password: string) => Promise<CustomClaims | null>;
  loginWithGoogle: () => Promise<CustomClaims | null>;
  loginWithMicrosoft: () => Promise<CustomClaims | null>;
  register: (email: string, password: string, role?: string) => Promise<void>;
  logout: () => Promise<void>;
  // Token refresh
  refreshUserToken: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  refreshUserClaims: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const dispatch = useAppDispatch();
  const [user, setUser] = useState<User | null>(null);
  const [userClaims, setUserClaims] = useState<CustomClaims | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const [error, setError] = useState('');

  // Helper to extract claims with retry logic and exponential backoff
  const extractCustomClaims = async (
    firebaseUser: User, 
    retries = 5,
    forceRefresh = false
  ): Promise<CustomClaims | null> => {
    authLog('[AUTH] Extracting custom claims, retries:', retries, 'forceRefresh:', forceRefresh);
    
    for (let i = 0; i < retries; i++) {
      try {
        // Force token refresh on first attempt or if requested
        const idTokenResult = await firebaseUser.getIdTokenResult(
          forceRefresh || i === 0
        );
        
        authLog(`[AUTH] Token result attempt ${i + 1}:`, {
          hasRole: !!idTokenResult.claims.role,
          hasTenantId: !!idTokenResult.claims.tenantId,
          claims: idTokenResult.claims
        });

        // Check if we have valid custom claims
        if (idTokenResult.claims.role && idTokenResult.claims.tenantId) {
          const claims: CustomClaims = {
            role: idTokenResult.claims.role as any,
            tenantId: idTokenResult.claims.tenantId as string,
            permissions: idTokenResult.claims.permissions as string[] || [],
            isSuperAdmin: idTokenResult.claims.isSuperAdmin as boolean || false,
            name: (idTokenResult.claims.name as string) || undefined,
            email: (idTokenResult.claims.email as string) || undefined
          };
          
          authLog('[AUTH] Claims extracted successfully:', claims);
          setUserClaims(claims);
          setError(''); // Clear any previous errors
          return claims;
        }
        
        // If no claims yet, wait longer between retries
        if (i < retries - 1) {
          const waitTime = Math.min(1000 * Math.pow(2, i), 5000); // Exponential backoff
          authLog(`[AUTH] No claims yet, waiting ${waitTime}ms before retry`);
          await new Promise(resolve => setTimeout(resolve, waitTime));
        }
      } catch (error) {
        console.error(`[AUTH] Attempt ${i + 1} failed:`, error);
      }
    }
    
    console.error('[AUTH] Failed to extract claims after', retries, 'attempts');
    return null;
  };

  // Fetch user profile from backend
  const fetchUserProfile = async () => {
    if (!user) {
      authLog('[AUTH] No user available for profile fetch');
      return;
    }

    setProfileLoading(true);
    authLog('[AUTH] Fetching user profile for:', user.email);

    // The ID token's standard `name` claim carries the OAuth account's display name even
    // when firebase user.displayName is null (some OAuth accounts don't populate it).
    // Read it from the cached token, not userClaims state (stale closure hazard).
    let tokenName: string | undefined;
    try {
      const tokenResult = await user.getIdTokenResult(false);
      tokenName = (tokenResult.claims.name as string) || undefined;
    } catch {
      tokenName = undefined;
    }

    try {
      // First ensure we have a valid token
      const token = await getIdToken(false);
      if (!token) {
        console.error('[AUTH] No token available for profile fetch');
        setError('Authentication token not available');
        return;
      }
      authLog('[AUTH] Token obtained for profile fetch');

      const profileResponse = await api.get<UserProfile>('/user/profile');
      authLog('[AUTH] Profile response:', profileResponse);

      if (profileResponse.success && profileResponse.data) {
        authLog('[AUTH] Profile fetched successfully:', profileResponse.data);
        authLog('[AUTH] Firebase user.displayName:', user.displayName);
        authLog('[AUTH] Backend profileResponse.data.displayName:', profileResponse.data.displayName);

        // Use backend displayName, fall back to Firebase user's displayName (from Google/Microsoft
        // OAuth), then the token's `name` claim, before degrading to the email localpart.
        const effectiveDisplayName = profileResponse.data.displayName
          || user.displayName
          || tokenName
          || user.email?.split('@')[0]
          || 'User';

        authLog('[AUTH] Effective displayName chosen:', effectiveDisplayName);

        setUserProfile({
          ...profileResponse.data,
          displayName: effectiveDisplayName
        });
      } else {
        console.warn('[AUTH] Profile response was unsuccessful or empty');
        // Create a default profile from Firebase user
        setUserProfile({
          id: user.uid,
          email: user.email || '',
          displayName: user.displayName || tokenName || user.email?.split('@')[0] || 'User',
          role: userClaims?.role || 'student',
          tenantId: userClaims?.tenantId || 'default'
        });
      }
    } catch (error: any) {
      console.error('[AUTH] Profile fetch error:', error);

      // If profile doesn't exist yet, create a default one
      if (error.status === 404) {
        authLog('[AUTH] Profile not found (404), creating default profile');
        setUserProfile({
          id: user.uid,
          email: user.email || '',
          displayName: user.displayName || tokenName || user.email?.split('@')[0] || 'User',
          role: userClaims?.role || 'student',
          tenantId: userClaims?.tenantId || 'default'
        });
      } else {
        console.error('[AUTH] Unexpected profile fetch error:', error.message);
        // Still create a default profile to allow app to function
        setUserProfile({
          id: user.uid,
          email: user.email || '',
          displayName: user.displayName || tokenName || user.email?.split('@')[0] || 'User',
          role: userClaims?.role || 'student',
          tenantId: userClaims?.tenantId || 'default'
        });
      }
    } finally {
      setProfileLoading(false);
    }
  };

  // Hydrate coaching progress from backend
  const hydrateUserProgress = async (uid: string) => {
    try {
      const data = await userProgressService.getProgress(uid);
      const discovered = Object.keys(data.gaps || {});
      // Update Redux totals + discovered IDs
      dispatch(hydrateCoaching({
        totalPoints: typeof data.totalPoints === 'number' ? data.totalPoints : 0,
        completedQuizzes: Array.isArray(data.completedQuizzes) ? data.completedQuizzes : [],
        discoveredGaps: discovered,
      }));
      // Hydrate in-memory gap map for details shown on Dashboard
      GapDiscoveryService.hydrateFromServer(uid, data.gaps);
    } catch (e) {
      console.warn('[Auth] Failed to hydrate user progress:', e);
    }
  };

  // Register with proper token refresh
  const register = async (email: string, password: string, role: string = 'student') => {
    setLoading(true);
    setError('');
    
    try {
      // Step 1: Call backend registration endpoint
      const response = await authApi.register({
        email,
        password,
        role
      });
      
      // Step 2: Sign in the user
      const userCredential = await signInWithEmail(email, password);
      
      // Step 3: If backend indicated token refresh required, sign out and back in
      if (response.data?.requiresTokenRefresh) {
        await logOut();
        await signInWithEmail(email, password);
      }
      
      // Step 4: Extract claims with more retries and force refresh
      const claims = await extractCustomClaims(
        userCredential.user, 
        10, // More retries for registration
        true // Force refresh
      );
      
      if (!claims) {
        // Try calling the refresh endpoint
        try {
          const token = await userCredential.user.getIdToken();
          await authApi.refreshClaims(token);
          
          // Sign out and back in to get new token
          await logOut();
          const newCredential = await signInWithEmail(
            email,
            password
          );
          
          // Try once more
          const refreshedClaims = await extractCustomClaims(
            newCredential.user,
            5,
            true
          );
          
          if (refreshedClaims) {
            await fetchUserProfile();
            toast.success('Welcome to DigiStore!');
            return;
          }
        } catch (refreshError) {
          console.error('[AUTH] Refresh claims failed:', refreshError);
        }
        
        setError('Account setup is in progress. Please try signing in again in a moment.');
        toast.error('Account setup is in progress. Please try signing in again in a moment.');
      } else {
        await fetchUserProfile();
        toast.success('Welcome to DigiStore!');
      }
    } catch (error: any) {
      const message = error.message || 'Registration failed';
      setError(message);
      toast.error(message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Login with token refresh — returns claims for role-based redirect
  const login = async (email: string, password: string): Promise<CustomClaims | null> => {
    try {
      setError('');
      setLoading(true);

      authLog('[AUTH] Starting login for:', email);

      const userCredential = await signInWithEmail(email, password);
      authLog('[AUTH] Firebase login successful:', userCredential.user.email);

      // Always refresh token on login to get latest claims
      let claims = await extractCustomClaims(userCredential.user, 5, true);
      authLog('[AUTH] Extracted claims:', claims);

      if (!claims) {
        console.error('[AUTH] No claims found after login');

        // Try to refresh claims via API
        try {
          authLog('[AUTH] Attempting to refresh claims via API');
          const token = await userCredential.user.getIdToken();
          await authApi.refreshClaims(token);

          // Force token refresh
          await userCredential.user.getIdToken(true);

          // Try extracting claims again
          claims = await extractCustomClaims(userCredential.user, 5, true);

          if (claims) {
            authLog('[AUTH] Claims refreshed successfully:', claims);
            await fetchUserProfile();
            toast.success('Welcome back!');
            return claims;
          }
        } catch (refreshError) {
          console.error('[AUTH] Failed to refresh claims:', refreshError);
        }

        setError('Your account is being set up. Please try again in a moment.');
        toast.error('Your account is being set up. Please try again in a moment.');

        // Don't throw error to allow user to stay logged in
        return null;
      } else {
        // Fetch profile
        authLog('[AUTH] Fetching user profile');
        await fetchUserProfile();
        if (userCredential.user?.uid) {
          await hydrateUserProgress(userCredential.user.uid);
        }
        authLog('[AUTH] Login complete');
        toast.success('Welcome back!');
        return claims;
      }

    } catch (error: any) {
      console.error('[AUTH] Login error:', error);

      // Provide more specific error messages
      let message = 'Login failed';

      if (error.code === 'auth/user-not-found') {
        message = 'No account found with this email address';
      } else if (error.code === 'auth/wrong-password') {
        message = 'Incorrect password';
      } else if (error.code === 'auth/invalid-email') {
        message = 'Invalid email address';
      } else if (error.code === 'auth/user-disabled') {
        message = 'This account has been disabled';
      } else if (error.code === 'auth/too-many-requests') {
        message = 'Too many failed attempts. Please try again later';
      } else if (error.message) {
        message = error.message;
      }

      setError(message);
      toast.error(message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Social login handler (shared logic) — returns claims for role-based redirect
  const handleSocialLogin = async (userCredential: UserCredential): Promise<CustomClaims | null> => {
    authLog('[AUTH] Social login successful:', userCredential.user.email);

    // Try to get claims - social users may not have claims initially
    const claims = await extractCustomClaims(userCredential.user, 5, true);
    authLog('[AUTH] Social login claims:', claims);

    if (!claims) {
      // Claims not available yet — the backend's auth middleware will repair
      // them on the next API request and signal a token refresh. Don't fake
      // claims here; return null so the caller can handle appropriately.
      console.warn('[AUTH] No claims for social user after retries — account being set up');
      setError('Your account is being set up. Please try again in a moment.');
      toast.error('Your account is being set up. Please try again in a moment.');
      return null;
    }

    await fetchUserProfile();
    if (userCredential.user?.uid) {
      await hydrateUserProgress(userCredential.user.uid);
    }
    toast.success('Welcome!');
    return claims;
  };

  // Google login — returns claims for role-based redirect
  const loginWithGoogle = async (): Promise<CustomClaims | null> => {
    try {
      setError('');
      setLoading(true);

      authLog('[AUTH] Starting Google login');

      const auth = await getFirebaseAuth();
      const provider = new GoogleAuthProvider();
      provider.addScope('email');
      provider.addScope('profile');

      try {
        const userCredential = await signInWithPopup(auth, provider);
        return await handleSocialLogin(userCredential);
      } catch (popupError: any) {
        // Fall back to redirect for popup-related failures
        if (popupError.code === 'auth/popup-blocked' ||
            popupError.code === 'auth/popup-closed-by-user' ||
            popupError.code === 'auth/cancelled-popup-request') {
          authLog('[AUTH] Popup failed, falling back to redirect:', popupError.code);
          toast('Redirecting to Google sign-in...', { icon: '🔄' });
          await signInWithRedirect(auth, provider);
          // signInWithRedirect navigates away; return null (won't be reached)
          return null;
        }
        throw popupError;
      }

    } catch (error: any) {
      console.error('[AUTH] Google login error:', error);

      let message = 'Google sign-in failed';
      if (error.code === 'auth/popup-closed-by-user') {
        message = 'Sign-in cancelled';
      } else if (error.code === 'auth/popup-blocked') {
        message = 'Pop-up blocked. Please allow pop-ups for this site';
      } else if (error.code === 'auth/account-exists-with-different-credential') {
        message = 'An account already exists with the same email. Try a different sign-in method';
      } else if (error.code === 'auth/unauthorized-domain' ||
                 error.message?.includes('requested action is invalid')) {
        message = 'Sign-in is temporarily unavailable. Please contact your instructor.';
      } else if (error.message) {
        message = error.message;
      }

      setError(message);
      toast.error(message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Microsoft login — returns claims for role-based redirect
  const loginWithMicrosoft = async (): Promise<CustomClaims | null> => {
    try {
      setError('');
      setLoading(true);

      authLog('[AUTH] Starting Microsoft login');

      const auth = await getFirebaseAuth();
      const provider = new OAuthProvider('microsoft.com');
      provider.addScope('email');
      provider.addScope('profile');

      try {
        const userCredential = await signInWithPopup(auth, provider);
        return await handleSocialLogin(userCredential);
      } catch (popupError: any) {
        // Fall back to redirect for popup-related failures
        if (popupError.code === 'auth/popup-blocked' ||
            popupError.code === 'auth/popup-closed-by-user' ||
            popupError.code === 'auth/cancelled-popup-request') {
          authLog('[AUTH] Popup failed, falling back to redirect:', popupError.code);
          toast('Redirecting to Microsoft sign-in...', { icon: '🔄' });
          await signInWithRedirect(auth, provider);
          return null;
        }
        throw popupError;
      }

    } catch (error: any) {
      console.error('[AUTH] Microsoft login error:', error);

      let message = 'Microsoft sign-in failed';
      if (error.code === 'auth/popup-closed-by-user') {
        message = 'Sign-in cancelled';
      } else if (error.code === 'auth/popup-blocked') {
        message = 'Pop-up blocked. Please allow pop-ups for this site';
      } else if (error.code === 'auth/account-exists-with-different-credential') {
        message = 'An account already exists with the same email. Try a different sign-in method';
      } else if (error.code === 'auth/unauthorized-domain' ||
                 error.message?.includes('requested action is invalid')) {
        message = 'Sign-in is temporarily unavailable. Please contact your instructor.';
      } else if (error.message) {
        message = error.message;
      }

      setError(message);
      toast.error(message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Manual token refresh
  const refreshUserToken = async () => {
    if (!user) return;
    
    try {
      // Force token refresh to get latest claims
      const claims = await extractCustomClaims(user, 3, true);
      
      if (claims) {
        setError('');
        toast.success('Permissions refreshed successfully');
      } else {
        toast.error('Unable to refresh permissions');
      }
    } catch (error) {
      // Error logging removed for security
      toast.error('Failed to refresh permissions');
    }
  };

  // Refresh profile
  const refreshProfile = async () => {
    await fetchUserProfile();
  };

  // Logout
  const logout = async () => {
    try {
      // Import dynamically to avoid circular dependencies
      const { completeAllActiveConversations } = await import('../utils/conversationUtils');
      
      // Complete all active conversations before logging out
      try {
        await completeAllActiveConversations();
      } catch (error) {
        console.error('[Auth] Error completing conversations:', error);
        // Continue with logout even if completion fails
      }
      
      await logOut();
      setUserClaims(null);
      setUserProfile(null);
      setError('');
      toast.success('Signed out successfully');
    } catch (error) {
      console.error(error);
      toast.error('Failed to sign out');
    }
  };

  // Manual claims refresh function
  const refreshUserClaims = async () => {
    if (!user) return false;

    try {
      setLoading(true);
      const token = await user.getIdToken();
      await authApi.refreshClaims(token);
      await user.getIdToken(true); // force refresh to pick up new claims
      const claims = await extractCustomClaims(user, 3, true);
      return !!claims;
    } catch (error) {
      console.error('[AUTH] Failed to refresh claims:', error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Monitor auth state with automatic token refresh
  useEffect(() => {
    let unsubscribe: (() => void) | null = null;
    
    const setupAuth = async () => {
      try {
        // Initialize Firebase first
        await initializeFirebase();

        // Complete a pending magic-link (passwordless email) sign-in, if the URL carries one.
        // Mirrors the getRedirectResult pattern; onAuthChange below then observes the new user.
        try {
          await completeMagicLinkIfPresent();
        } catch (magicErr) {
          console.error('[AUTH] Magic-link completion error:', magicErr);
        }

        // Handle redirect result (from signInWithRedirect fallback)
        try {
          const auth = await getFirebaseAuth();
          const redirectResult = await getRedirectResult(auth);
          if (redirectResult) {
            authLog('[AUTH] Redirect sign-in completed for:', redirectResult.user.email);
            setUser(redirectResult.user);
            const claims = await extractCustomClaims(redirectResult.user, 5, true);
            if (claims) {
              await fetchUserProfile();
              if (redirectResult.user.uid) {
                await hydrateUserProgress(redirectResult.user.uid);
              }
              toast.success('Welcome!');
            }
          }
        } catch (redirectError: any) {
          // Don't block auth setup if redirect result fails
          console.error('[AUTH] Redirect result error:', redirectError);
          if (redirectError.code === 'auth/account-exists-with-different-credential') {
            setError('An account already exists with the same email. Try a different sign-in method.');
            toast.error('An account already exists with the same email. Try a different sign-in method.');
          }
        }

        // Then set up the auth listener
        unsubscribe = await onAuthChange(async (firebaseUser) => {
          if (firebaseUser) {
            setUser(firebaseUser);
            
            // Start token monitoring for automatic refresh
            startTokenMonitoring({
              onTokenExpired: () => {
                authLog('[AUTH] Token expired, redirecting to login');
                window.location.href = '/login';
              },
              onTokenRefreshed: async (token) => {
                authLog('[AUTH] Token was refreshed');
                // Re-extract claims after token refresh
                const claims = await extractCustomClaims(firebaseUser, 1, false);
                if (claims) {
                  await fetchUserProfile();
                  if (firebaseUser.uid) {
                    await hydrateUserProgress(firebaseUser.uid);
                  }
                }
              },
              onTokenError: (error) => {
                console.error('[AUTH] Token monitor error:', error);
                setError('Authentication error. Please sign in again.');
              }
            });
            
            // Try to get custom claims
            let claims = await extractCustomClaims(firebaseUser);

            if (!claims) {
              // Token refresh + retry before falling back
              authLog('[AUTH] No claims on initial load, forcing token refresh');
              try {
                await firebaseUser.getIdToken(true);
                claims = await extractCustomClaims(firebaseUser, 3, true);
              } catch (retryErr) {
                console.error('[AUTH] Token refresh retry failed:', retryErr);
              }
            }

            if (!claims) {
              console.error('[AUTH] No claims after retry — account may need setup');
              setError('Your account is being configured. Please sign out and try again.');
            }

            setError('');
            // Always fetch profile - displayName comes from Firebase user, not claims
            await fetchUserProfile();
            if (firebaseUser.uid) {
              await hydrateUserProgress(firebaseUser.uid);
            }
          } else {
            // Stop token monitoring when user signs out
            stopTokenMonitoring();
            setUser(null);
            setUserClaims(null);
            setUserProfile(null);
            setError('');
          }
          
          setLoading(false);
        });
      } catch (error) {
        console.error('Failed to initialize Firebase auth:', error);
        setError('Failed to initialize authentication');
        setLoading(false);
      }
    };
    
    setupAuth();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  const value: AuthContextType = {
    user,
    userClaims,
    userProfile,
    loading,
    profileLoading,
    error,
    login,
    loginWithGoogle,
    loginWithMicrosoft,
    register,
    logout,
    refreshUserToken,
    refreshProfile,
    refreshUserClaims
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Legacy exports for compatibility
export const useFirebaseUser = () => {
  const { user } = useAuth();
  return user;
};

export const useUserClaims = () => {
  const { userClaims } = useAuth();
  return userClaims;
};
